import React, {
  Fragment,
  useEffect,
  useState
} from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getAll } from "../../actions/airports/creators";
import { Airport } from "../../actions/airports/types";
import { RootState } from "../../reducers";
import { SM, ShowWindowDimensions } from "../../utils/mobileQueries";
import { compareTwoStrings } from 'string-similarity';

import * as SC from "./style";

const AUTH_REDIRECT_STORAGE_KEY = "auth_redirect_path";

interface Props {
  mobileMenu?: boolean;
  setMenuOpen?: any;
  inputFocus?: boolean;
  setInputFocus?: any;
}

const SearchBar: React.FC<Props> = ({
  mobileMenu,
  setMenuOpen,
  inputFocus,
  setInputFocus
}) => {
  let location = useLocation();
  const history = useHistory();
  const isLogged = useSelector((state: RootState) => state.auth.isLogged);

  const { t } = useTranslation();

  const [airports, setAirports] = useState<Airport[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [active, setActive] = useState(false);
  const handleChange = (event: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setSearchTerm(event.target.value);
  };
  const [searchResults, setSearchResults] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const _airports = await getAll();
      setAirports(_airports);
    };

    fetchData();
  }, []);

  const isResultsRoute = () => location.pathname.split("/")[1] === "resultados";

  const getResultPath = (airportCode: string) => `/resultados/${airportCode}`;

  const getDestination = (airportCode: string) => {
    const resultPath = getResultPath(airportCode);

    return isLogged
      ? { pathname: resultPath }
      : { pathname: "/login", state: { from: { pathname: resultPath } } };
  };

  const storePendingRedirect = (airportCode: string) => {
    if (!isLogged) {
      sessionStorage.setItem(AUTH_REDIRECT_STORAGE_KEY, getResultPath(airportCode));
    }
  };

  const scheduleReload = () => {
    if (isLogged && isResultsRoute()) {
      setTimeout(() => {
        window.location.reload();
      }, 400);
    }
  };

  const normalize = (value: string) =>
    value.toLowerCase()
    .replace("á", "a")
    .replace("é", "e")
    .replace("í", "i")
    .replace("ó", "o")
    .replace("ú", "u");

  useEffect(() => {
    const results = [...airports].filter((a) => {
      const a1 = normalize(`${a.airportCode} ${a.airportName} ${a.city} ${a.state} ${a.country}`);
      const s = normalize(searchTerm);

      return a1.includes(s);
    })

    const results2 = [...airports].filter((a) => !results.includes(a)).sort((a, b) => {
      const a1 = normalize(`${a.airportCode} ${a.airportName} ${a.city} ${a.state} ${a.country}`);
      const b1 = normalize(`${b.airportCode} ${b.airportName} ${b.city} ${b.state} ${b.country}`);
      const s = normalize(searchTerm);

      return compareTwoStrings(b1, s) - compareTwoStrings(a1, s);
    }).slice(0, 6 - results.length);

    setSearchResults([...results, ...results2]);
  }, [airports, searchTerm]);

  const storeAirport = (airport: Airport) => {
    localStorage.setItem("airport", JSON.stringify(airport));
  };

  const navigateToAirport = (airport: Airport) => {
    storeAirport(airport);
    storePendingRedirect(airport.airportCode);
    history.push(getDestination(airport.airportCode));
    scheduleReload();
  };

  const keyControlEvent = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (searchTerm === "") return;
    const ul = document.getElementById("menu") as HTMLUListElement;
    const { key } = event;
    if (key !== "ArrowDown" && key !== "ArrowUp" && key !== "Enter") return;
    event.preventDefault();
    const lis: HTMLLIElement[] | undefined = Array.from(
      ul!.querySelectorAll("li.hoverable")
    );
    if (key === "ArrowDown") {
      if ((window as any).currentFocusIndex === null) {
        (window as any).currentFocusIndex = 0;
      } else {
        if ((window as any).currentFocusIndex < lis.length - 1) {
          (window as any).currentFocusIndex++;
        }
      }
    }
    if (key === "ArrowUp") {
      if ((window as any).currentFocusIndex! > 0) {
        (window as any).currentFocusIndex!--;
      } else {
        (window as any).currentFocusIndex = null;
      }
    }
    lis.forEach(li => {
      li.classList.remove("key-hover");
    });

    if (
      (window as any).currentFocusIndex !== null &&
      lis[(window as any).currentFocusIndex]
    ) {
      lis[(window as any).currentFocusIndex].classList.add("key-hover");
      lis[(window as any).currentFocusIndex].focus();
      if (key === "Enter") {
        navigateToAirport(searchResults[(window as any).currentFocusIndex]);
      }
    }
  };

  useEffect(() => {
    (window as any).currentFocusIndex = null;
  }, []);

  useEffect(() => {
    function handleKeyDown(e: any) {
      if (e.key === "Enter" && searchResults.length === 1) {
        const airport = searchResults[0];
        const resultPath = `/resultados/${airport.airportCode}`;

        localStorage.setItem("airport", JSON.stringify(airport));

        if (!isLogged) {
          sessionStorage.setItem(AUTH_REDIRECT_STORAGE_KEY, resultPath);
          history.push({
            pathname: "/login",
            state: { from: { pathname: resultPath } }
          });
          return;
        }

        history.push({ pathname: resultPath });

        if (location.pathname.split("/")[1] === "resultados") {
          setTimeout(() => {
            window.location.reload();
          }, 400);
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return function cleanup() {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [history, isLogged, location.pathname, searchResults]);

  return (
    <SC.RelativeWrap>
      {!mobileMenu ? (
        <Fragment>
          <SC.SearchWrap>
            <SC.Wrapper>
              <SC.SearchIcon />

              {inputFocus && (
                <SC.Input
                  id="search-input"
                  onKeyDown={keyControlEvent}
                  type="text"
                  placeholder={
                    ShowWindowDimensions() >= SM
                      ? t("homepage.buscador")
                      : t("homepage.buscador_mobile")
                  }
                  value={searchTerm}
                  onChange={handleChange}
                  autoComplete="off"
                  autoFocus={true}
                  onBlur={() => {
                    setInputFocus(false);
                  }}
                />
              )}

              {!inputFocus && (
                <SC.Input
                  id="search-input"
                  onKeyDown={keyControlEvent}
                  type="text"
                  placeholder={
                    ShowWindowDimensions() >= SM
                      ? t("homepage.buscador")
                      : t("homepage.buscador_mobile")
                  }
                  value={searchTerm}
                  onChange={handleChange}
                  autoComplete="off"
                  onBlur={() => {
                    setInputFocus(false);
                  }}
                />
              )}
            </SC.Wrapper>
          </SC.SearchWrap>
          <SC.Submenu id="menu" searchTerm={searchTerm}>
            {searchResults.length !== 0 ? (
              <>
                {searchResults
                  .map((item, i) => {
                    if (searchTerm !== "" && searchResults.length > 1) {
                      return (
                        <li key={i} className="hoverable">
                          <Link
                            onClick={() => {
                              storeAirport(item);
                              storePendingRedirect(item.airportCode);
                              scheduleReload();
                            }}
                            key={i}
                            to={getDestination(item.airportCode)}
                          >
                            {}
                            <SC.Card>
                              <SC.BgPic bg={item.image} />
                              <SC.Headline>
                                <h3>
                                  {item.airportCode} - {item.airportName}
                                </h3>
                                <h4>
                                  {[item.city, item.state, item.country].join(
                                    ", "
                                  )}
                                </h4>
                              </SC.Headline>
                            </SC.Card>
                          </Link>
                        </li>
                      );
                    } else {
                      return (
                        <li key={i} className="unique" tabIndex={0}>
                          <Link
                            onClick={() => {
                              storeAirport(item);
                              storePendingRedirect(item.airportCode);
                              scheduleReload();
                            }}
                            key={i}
                            to={getDestination(item.airportCode)}
                          >
                            <SC.Card>
                              <SC.BgPic bg={item.image} />
                              <SC.Headline>
                                <h3>
                                  {item.airportCode} - {item.airportName}
                                </h3>
                                <h4>
                                  {[item.city, item.state, item.country].join(
                                    ", "
                                  )}
                                </h4>
                              </SC.Headline>
                            </SC.Card>
                          </Link>
                        </li>
                      );
                    }
                  })}
              </>
            ) : (
              <>
                <SC.Card>
                  <h5>{t("generales.ningun_aeropuerto")}</h5>
                </SC.Card>
              </>
            )}
          </SC.Submenu>
        </Fragment>
      ) : (
        <Fragment>
          <SC.MenuSearchWrap active={active} onClick={() => setActive(!active)}>
            <SC.Wrapper>
              <SC.MenuSearchIcon />
              <SC.MenuInput
                type="text"
                placeholder={t("homepage.buscador_mobile")}
                value={searchTerm}
                onChange={handleChange}
              />
            </SC.Wrapper>
          </SC.MenuSearchWrap>

          <SC.MenuSubmenu searchTerm={searchTerm}>
            {searchResults.length !== 0 ? (
              <>
                {searchResults
                  .map((item, i) => {
                    if (searchTerm !== "") {
                      return (
                        <Link
                          onClick={() => {
                            storeAirport(item);
                            storePendingRedirect(item.airportCode);
                            scheduleReload();
                            setMenuOpen(false);
                          }}
                          key={i}
                          to={getDestination(item.airportCode)}
                        >
                          <SC.Card>
                            <SC.BgPic bg={item.image} />
                            <SC.Headline>
                              <h3>
                                {item.airportCode} - {item.airportName}
                              </h3>
                              <h4>
                                {[item.city, item.state, item.country].join(
                                  ", "
                                )}
                              </h4>
                            </SC.Headline>
                          </SC.Card>
                        </Link>
                      );
                    }

                    return null;
                  })}
              </>
            ) : (
              <>
                <SC.Card>
                  <h6>{t("generales.ningun_aeropuerto")}</h6>
                </SC.Card>
              </>
            )}
          </SC.MenuSubmenu>
        </Fragment>
      )}
    </SC.RelativeWrap>
  );
};

export default SearchBar;
